import { Botao } from '@mymaxx/ui';
import { Coffee } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * A tela de quem está em pausa.
 *
 * Cobre tudo e não fecha: em pausa o atendente não recebe cliente, e deixá-lo mexer no
 * sistema como se estivesse trabalhando é o que produz o pior dos casos — responder uma
 * conversa "de rapidinho" durante o almoço e o tempo dela contar como atendimento parado.
 *
 * Ausente é diferente: quem está ausente pode navegar à vontade, só não recebe da fila.
 * Por isso este bloqueio vale apenas para a pausa.
 */
export function ModalPausa({
  motivo,
  desdeIso,
  voltando,
  onVoltar,
}: {
  motivo: string | null;
  /** Instante em que a pausa começou (ISO). Sem ele, o relógio começa agora. */
  desdeIso: string | null;
  voltando?: boolean;
  onVoltar: () => void;
}) {
  const decorrido = useDecorrido(desdeIso);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Você está em pausa"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/80 p-6 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm space-y-5 rounded-2xl bg-white p-8 text-center shadow-2xl dark:bg-neutral-900">
        <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
          <Coffee size={26} />
        </div>

        <div className="space-y-1">
          <p className="text-sm text-neutral-500">Você está em pausa</p>
          <p className="text-2xl font-semibold">{motivo ?? 'Pausa'}</p>
        </div>

        <p className="font-mono text-4xl tabular-nums" aria-live="polite">
          {relogio(decorrido)}
        </p>

        <Botao className="w-full" carregando={voltando} onClick={onVoltar}>
          Voltar ao atendimento
        </Botao>

        <p className="text-xs text-neutral-500">
          Enquanto estiver em pausa você não recebe clientes da fila.
        </p>
      </div>
    </div>
  );
}

/** Segundos decorridos desde o início da pausa, contados de segundo em segundo. */
function useDecorrido(desdeIso: string | null): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!desdeIso) return 0;
  return Math.max(0, Math.floor((agora - new Date(desdeIso).getTime()) / 1000));
}

/** HH:MM:SS, porque numa pausa longa "127min" não diz nada a ninguém. */
function relogio(segundos: number): string {
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;
  const dois = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${dois(h)}:${dois(m)}:${dois(s)}` : `${dois(m)}:${dois(s)}`;
}
